import type { ReactNode } from "react";
import type { IDetailCellRendererParams } from "ag-grid-community";
import { cn } from "@synthesis/shared-utils";

import type { ConstructorDocumentKit } from "../constructor-documents-page.lib";

/** «Виды работ: …» — подпись серым, значение обычным текстом (как в макете). */
const splitWorkTypesNote = (note: string): { label: string; text: string } => {
  const separatorIndex = note.indexOf(":");

  if (separatorIndex === -1) {
    return { label: "", text: note };
  }

  return { label: note.slice(0, separatorIndex + 1), text: note.slice(separatorIndex + 1).trim() };
};

/** Ячейка вложенной таблицы: текст в одну строку с обрезанием по троеточию. */
const DocumentKitDetailCell = ({ children, className }: { children: ReactNode; className?: string }) => (
  <td className={cn("paragraph-sm text-type-common px-2 py-[3px] align-top", className)}>
    <span className="block truncate">{children}</span>
  </td>
);

/**
 * Панель раскрытой строки таблицы комплектов документов (master/detail AG Grid).
 * Белая карточка со списком документов комплекта («№ / Документ / Описание / Обязательность»)
 * и подпись «Виды работ» — по макету «Конструктор — Документы».
 */
export const DocumentKitDetailPanel = (params: IDetailCellRendererParams<ConstructorDocumentKit>) => {
  const expanded = params.data?.expanded;

  if (!expanded) {
    return null;
  }

  const workTypes = splitWorkTypesNote(expanded.workTypesNote);

  return (
    <div className="bg-neutrals-99 flex flex-col gap-2 px-[18px] py-4">
      <div className="bg-neutrals-white border-neutrals-95 rounded-xl border px-3 py-[6px] shadow-sm">
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            <col className="w-[45px]" />
            <col className="w-[338px]" />
            <col />
            <col className="w-[160px]" />
          </colgroup>
          <tbody>
            {expanded.documents.map((document, index) => (
              <tr key={document.name}>
                <DocumentKitDetailCell>{index + 1}</DocumentKitDetailCell>
                <DocumentKitDetailCell>{document.name}</DocumentKitDetailCell>
                <DocumentKitDetailCell>{document.description}</DocumentKitDetailCell>
                <DocumentKitDetailCell className={document.required ? "text-type-common" : "text-type-light"}>
                  {document.required ? "Обязателен" : "По желанию"}
                </DocumentKitDetailCell>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="subtitle-base px-1">
        {workTypes.label ? <span className="text-type-light">{workTypes.label} </span> : null}
        <span className="text-type-minor">{workTypes.text}</span>
      </p>
    </div>
  );
};
